import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Trophy,
  Medal,
  Star,
  Crown,
  Flame,
  Target,
  TrendingUp,
  Users,
  DollarSign,
  Award,
  Zap,
  Gift,
  Calendar,
  ArrowUp,
  Sparkles,
  Rocket,
  Heart,
  ThumbsUp,
  PartyPopper
} from 'lucide-react';
import api from '../services/api';
import confetti from 'canvas-confetti';

// Achievement definitions
const ACHIEVEMENTS = {
  first_referral: { name: 'First Steps', description: 'Got your first referral', icon: Star, color: 'text-yellow-500', threshold: 1 },
  five_referrals: { name: 'Rising Star', description: 'Reached 5 referrals', icon: TrendingUp, color: 'text-blue-500', threshold: 5 },
  ten_referrals: { name: 'Influencer', description: 'Reached 10 referrals', icon: Users, color: 'text-purple-500', threshold: 10 },
  twenty_five_referrals: { name: 'Ambassador', description: 'Reached 25 referrals', icon: Award, color: 'text-indigo-500', threshold: 25 },
  fifty_referrals: { name: 'Champion', description: 'Reached 50 referrals', icon: Trophy, color: 'text-amber-500', threshold: 50 },
  hundred_referrals: { name: 'Legend', description: 'Reached 100 referrals', icon: Crown, color: 'text-orange-500', threshold: 100 },
  first_payout: { name: 'Payday', description: 'Received your first payout', icon: DollarSign, color: 'text-green-500' },
  thousand_earned: { name: 'Big Earner', description: 'Earned $1,000 in commissions', icon: Zap, color: 'text-emerald-500', threshold: 1000 },
  five_thousand_earned: { name: 'Top Performer', description: 'Earned $5,000 in commissions', icon: Rocket, color: 'text-cyan-500', threshold: 5000 },
  streak_7: { name: 'Hot Streak', description: '7-day referral streak', icon: Flame, color: 'text-red-500', threshold: 7 },
  streak_30: { name: 'On Fire', description: '30-day referral streak', icon: Flame, color: 'text-orange-600', threshold: 30 },
  top_monthly: { name: 'Monthly Champion', description: 'Ranked #1 in a month', icon: Medal, color: 'text-gold-500' },
};

// Reward tiers
const REWARD_TIERS = [
  { name: 'Bronze Partner', minEarnings: 0, maxEarnings: 500, color: 'from-amber-600 to-amber-800', badge: '🥉', commission: 20 },
  { name: 'Silver Partner', minEarnings: 500, maxEarnings: 2000, color: 'from-slate-400 to-slate-600', badge: '🥈', commission: 22 },
  { name: 'Gold Partner', minEarnings: 2000, maxEarnings: 5000, color: 'from-yellow-400 to-yellow-600', badge: '🥇', commission: 25 },
  { name: 'Platinum Partner', minEarnings: 5000, maxEarnings: 15000, color: 'from-cyan-400 to-cyan-600', badge: '💎', commission: 28 },
  { name: 'Diamond Partner', minEarnings: 15000, maxEarnings: Infinity, color: 'from-purple-400 to-pink-500', badge: '👑', commission: 30 },
];

// Rank badge component
const RankBadge = ({ rank }) => {
  if (rank === 1) return <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-500 flex items-center justify-center shadow-lg"><Crown className="w-6 h-6 text-yellow-900" /></div>;
  if (rank === 2) return <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-300 to-slate-500 flex items-center justify-center shadow-lg"><Medal className="w-6 h-6 text-slate-900" /></div>;
  if (rank === 3) return <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center shadow-lg"><Medal className="w-6 h-6 text-amber-100" /></div>;
  return <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-600">{rank}</div>;
};

// Achievement card component
const AchievementCard = ({ achievement, unlocked, progress }) => {
  const Icon = achievement.icon;
  return (
    <div className={`p-4 rounded-xl border-2 transition-all ${unlocked ? 'border-emerald-300 bg-gradient-to-br from-emerald-50 to-green-50' : 'border-slate-200 bg-slate-50 opacity-60'}`}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${unlocked ? 'bg-emerald-100' : 'bg-slate-200'}`}>
          <Icon className={`w-6 h-6 ${unlocked ? achievement.color : 'text-slate-400'}`} />
        </div>
        <div className="flex-1">
          <p className={`font-semibold ${unlocked ? 'text-slate-900' : 'text-slate-500'}`}>{achievement.name}</p>
          <p className="text-xs text-slate-500">{achievement.description}</p>
        </div>
        {unlocked && <Sparkles className="w-5 h-5 text-emerald-500" />}
      </div>
      {progress !== undefined && !unlocked && (
        <div className="mt-3">
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-slate-500 mt-1">{Math.round(progress)}% complete</p>
        </div>
      )}
    </div>
  );
};

// Leaderboard row component
const LeaderboardRow = ({ reseller, rank, isCurrentUser }) => {
  const tier = REWARD_TIERS.find(t => reseller.total_earnings >= t.minEarnings && reseller.total_earnings < t.maxEarnings) || REWARD_TIERS[0];
  
  return (
    <div className={`flex items-center gap-4 p-4 rounded-xl transition-all ${isCurrentUser ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200' : 'bg-white hover:bg-slate-50 border border-slate-200'}`}>
      <RankBadge rank={rank} />
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-slate-900 truncate">{reseller.name}</p>
          <span className="text-lg">{tier.badge}</span>
          {reseller.streak > 0 && (
            <Badge className="bg-orange-100 text-orange-700 border-orange-200">
              <Flame className="w-3 h-3 mr-1" />{reseller.streak}d
            </Badge>
          )}
          {isCurrentUser && <Badge className="bg-blue-100 text-blue-700">You</Badge>}
        </div>
        <p className="text-sm text-slate-500 truncate">{reseller.email}</p>
      </div>
      
      <div className="text-right">
        <p className="font-bold text-emerald-600">${(reseller.total_earnings || 0).toFixed(2)}</p>
        <p className="text-xs text-slate-500">{reseller.referral_count || 0} referrals</p>
      </div>
      
      {rank <= 3 && (
        <div className="hidden md:block">
          {rank === 1 && <Trophy className="w-8 h-8 text-yellow-500" />}
          {rank === 2 && <Medal className="w-8 h-8 text-slate-400" />}
          {rank === 3 && <Medal className="w-8 h-8 text-amber-600" />}
        </div>
      )}
    </div>
  );
};

export const ResellerLeaderboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('leaderboard');
  const [timeframe, setTimeframe] = useState('all');
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  
  const isReseller = user?.role === 'reseller';
  const isAdmin = user?.role === 'system_admin';
  
  const loadLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/reseller/leaderboard?timeframe=${timeframe}`);
      setLeaderboard(response.data.leaderboard || []);
      setMyStats(response.data.my_stats || null);
      setAchievements(response.data.achievements || []);
      setRecentActivity(response.data.recent_activity || []);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, [timeframe]);
  
  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);
  
  // Celebrate achievements
  const celebrateAchievement = (achievementKey) => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
    toast.success(`🎉 Achievement Unlocked: ${ACHIEVEMENTS[achievementKey]?.name}!`);
  };
  
  // Get current tier info
  const getCurrentTier = () => {
    if (!myStats) return REWARD_TIERS[0];
    return REWARD_TIERS.find(t => myStats.total_earnings >= t.minEarnings && myStats.total_earnings < t.maxEarnings) || REWARD_TIERS[0];
  };
  
  const currentTier = getCurrentTier();
  const nextTier = REWARD_TIERS[REWARD_TIERS.indexOf(currentTier) + 1];
  const tierProgress = nextTier ? ((myStats?.total_earnings || 0) - currentTier.minEarnings) / (nextTier.minEarnings - currentTier.minEarnings) * 100 : 100;
  
  if (!isReseller && !isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Trophy className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Reseller Access Only</h2>
            <p className="text-slate-600">This leaderboard is only available to resellers and administrators.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            Partner Leaderboard
          </h1>
          <p className="text-slate-600">Compete, earn, and climb the ranks</p>
        </div>
        
        {/* My Stats Card (for resellers) */}
        {isReseller && myStats && (
          <Card className="bg-gradient-to-r from-blue-600 to-purple-600 text-white border-0 shadow-xl overflow-hidden">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                {/* Rank & Tier */}
                <div className="text-center">
                  <div className="text-6xl mb-2">{currentTier.badge}</div>
                  <p className="font-bold text-lg">{currentTier.name}</p>
                  <p className="text-blue-200 text-sm">{currentTier.commission}% commission</p>
                </div>
                
                {/* Stats */}
                <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-white/10 rounded-xl">
                    <p className="text-3xl font-bold">{myStats.rank || '-'}</p>
                    <p className="text-blue-200 text-sm">Your Rank</p>
                  </div>
                  <div className="text-center p-3 bg-white/10 rounded-xl">
                    <p className="text-3xl font-bold">{myStats.referral_count || 0}</p>
                    <p className="text-blue-200 text-sm">Referrals</p>
                  </div>
                  <div className="text-center p-3 bg-white/10 rounded-xl">
                    <p className="text-3xl font-bold">${(myStats.total_earnings || 0).toFixed(0)}</p>
                    <p className="text-blue-200 text-sm">Earned</p>
                  </div>
                  <div className="text-center p-3 bg-white/10 rounded-xl">
                    <div className="flex items-center justify-center gap-1">
                      <Flame className="w-6 h-6 text-orange-400" />
                      <p className="text-3xl font-bold">{myStats.streak || 0}</p>
                    </div>
                    <p className="text-blue-200 text-sm">Day Streak</p>
                  </div>
                </div>
                
                {/* Next Tier Progress */}
                {nextTier && (
                  <div className="w-full md:w-48">
                    <div className="flex justify-between text-sm mb-1">
                      <span>{currentTier.badge}</span>
                      <span>{nextTier.badge}</span>
                    </div>
                    <Progress value={tierProgress} className="h-3 bg-white/20" />
                    <p className="text-xs text-blue-200 mt-1 text-center">
                      ${(nextTier.minEarnings - (myStats.total_earnings || 0)).toFixed(0)} to {nextTier.name}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white/80 backdrop-blur border border-slate-200">
            <TabsTrigger value="leaderboard" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Trophy className="w-4 h-4 mr-2" />Leaderboard
            </TabsTrigger>
            <TabsTrigger value="achievements" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Award className="w-4 h-4 mr-2" />Achievements
            </TabsTrigger>
            <TabsTrigger value="rewards" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Gift className="w-4 h-4 mr-2" />Reward Tiers
            </TabsTrigger>
          </TabsList>
          
          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard" className="space-y-4">
            {/* Timeframe Filter */}
            <div className="flex gap-2">
              <Button 
                variant={timeframe === 'all' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setTimeframe('all')}
                className={timeframe === 'all' ? 'bg-blue-600' : ''}
              >
                All Time
              </Button>
              <Button 
                variant={timeframe === 'month' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setTimeframe('month')}
                className={timeframe === 'month' ? 'bg-blue-600' : ''}
              >
                This Month
              </Button>
              <Button 
                variant={timeframe === 'week' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setTimeframe('week')}
                className={timeframe === 'week' ? 'bg-blue-600' : ''}
              >
                This Week
              </Button>
            </div>
            
            {/* Leaderboard List */}
            <Card className="bg-white/80 backdrop-blur border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  Top Partners
                </CardTitle>
                <CardDescription>Ranked by total earnings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {loading ? (
                  <div className="text-center py-12 text-slate-500">Loading...</div>
                ) : leaderboard.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No partners on the leaderboard yet</p>
                  </div>
                ) : (
                  leaderboard.map((reseller, index) => (
                    <LeaderboardRow 
                      key={reseller.user_id} 
                      reseller={reseller} 
                      rank={index + 1}
                      isCurrentUser={reseller.user_id === user?.user_id}
                    />
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Achievements Tab */}
          <TabsContent value="achievements">
            <Card className="bg-white/80 backdrop-blur border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-purple-500" />
                  Achievements
                </CardTitle>
                <CardDescription>Unlock badges by reaching milestones</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(ACHIEVEMENTS).map(([key, achievement]) => {
                    const unlocked = achievements.includes(key);
                    let progress = 0;
                    
                    if (myStats && achievement.threshold) {
                      if (key.includes('referral')) {
                        progress = Math.min(100, (myStats.referral_count / achievement.threshold) * 100);
                      } else if (key.includes('earned')) {
                        progress = Math.min(100, (myStats.total_earnings / achievement.threshold) * 100);
                      } else if (key.includes('streak')) {
                        progress = Math.min(100, (myStats.streak / achievement.threshold) * 100);
                      }
                    }
                    
                    return (
                      <AchievementCard 
                        key={key}
                        achievement={achievement}
                        unlocked={unlocked}
                        progress={unlocked ? undefined : progress}
                      />
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Reward Tiers Tab */}
          <TabsContent value="rewards">
            <Card className="bg-white/80 backdrop-blur border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gift className="w-5 h-5 text-emerald-500" />
                  Partner Reward Tiers
                </CardTitle>
                <CardDescription>Earn more as you grow - higher tiers mean better commission rates</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {REWARD_TIERS.map((tier, index) => {
                    const isCurrentTier = currentTier.name === tier.name;
                    const isUnlocked = myStats && myStats.total_earnings >= tier.minEarnings;
                    
                    return (
                      <div 
                        key={tier.name}
                        className={`p-6 rounded-xl border-2 transition-all ${isCurrentTier ? 'border-blue-400 bg-blue-50 shadow-lg scale-[1.02]' : isUnlocked ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50 opacity-70'}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${tier.color} flex items-center justify-center text-3xl shadow-lg`}>
                            {tier.badge}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="text-xl font-bold text-slate-900">{tier.name}</h3>
                              {isCurrentTier && <Badge className="bg-blue-600">Current</Badge>}
                              {isUnlocked && !isCurrentTier && <Badge className="bg-emerald-100 text-emerald-700">Unlocked</Badge>}
                            </div>
                            <p className="text-slate-600">
                              {tier.maxEarnings === Infinity 
                                ? `$${tier.minEarnings.toLocaleString()}+ lifetime earnings`
                                : `$${tier.minEarnings.toLocaleString()} - $${tier.maxEarnings.toLocaleString()} lifetime earnings`
                              }
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-3xl font-bold text-emerald-600">{tier.commission}%</p>
                            <p className="text-sm text-slate-500">Commission Rate</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        {/* Recent Activity (for admins) */}
        {isAdmin && recentActivity.length > 0 && (
          <Card className="bg-white/80 backdrop-blur border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Recent Partner Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentActivity.map((activity, index) => (
                  <div key={`act-${index}-${activity.name || 'x'}-${activity.time || ''}`} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold">
                      {activity.name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{activity.name}</p>
                      <p className="text-sm text-slate-500">{activity.action}</p>
                    </div>
                    <p className="text-xs text-slate-400">{activity.time}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ResellerLeaderboard;
